"use client";

/**
 * Phase 9 / Plan 09-01 — Dual-fire client analytics for the setup-service
 * add-on.
 *
 * Every client-fired setup_service_* event is sent to BOTH:
 *   - GA4 via lib/analytics.ts (window.gtag)
 *   - PostHog via posthog-js (posthog.capture)
 *
 * The TWO `checkout_completed_with_setup_service` /
 * `checkout_completed_without_setup_service` events fire from the Stripe
 * webhook (server-side) using lib/analytics-server.ts + lib/posthog.ts
 * directly — they are NOT exposed by this client wrapper because Stripe
 * Checkout takes over the browser before the client sees the completion.
 *
 * Required payload context per CONTEXT.md D-03 + UI-SPEC Analytics Contract:
 *   { source_page, device_type, plan_selected, [cta_label] }
 */

import posthog from "posthog-js";
import { trackEvent } from "@/lib/analytics";
import { readAnalyticsConsent } from "@/components/analytics/browser-attribution";

export type SetupServiceEventName =
  | "setup_service_viewed"
  | "setup_service_cta_clicked"
  | "setup_service_selected"
  | "setup_service_deselected"
  // G3-8b — "Talk to us first" lead-capture path (Option A). Fired client-side
  // when the secondary lead form submits successfully; the server route emits a
  // separate PostHog `setup_service_lead_submitted` (keyed by email) too.
  | "setup_service_lead_submitted";

export type SetupServiceSourcePage =
  | "pricing"
  | "homepage"
  | "setup_for_you"
  | "checkout"
  | "post_purchase"
  | "settings_billing"
  | "dashboard"
  | "intake_form";

export type SetupServiceEventPayload = {
  source_page: SetupServiceSourcePage;
  device_type: "mobile" | "desktop";
  plan_selected?: "free" | "monthly" | "annual" | null;
  cta_label?: string;
};

/**
 * Derive device_type at event-fire time per UI-SPEC. Returns "desktop" on
 * SSR (window undefined) and "mobile" when viewport < 768px (Tailwind md).
 */
export function deriveDeviceType(): "mobile" | "desktop" {
  if (typeof window === "undefined") return "desktop";
  return window.innerWidth < 768 ? "mobile" : "desktop";
}

/**
 * Fire the named setup_service event to BOTH GA4 (lib/analytics) AND
 * PostHog. Each channel is wrapped in its own try/catch so a failure
 * in one stack never breaks the other (graceful degradation).
 */
export function trackSetupServiceEvent(
  name: SetupServiceEventName,
  payload: SetupServiceEventPayload,
): void {
  if (typeof window === "undefined" || readAnalyticsConsent() !== "accepted") {
    return;
  }

  // GA4 — trackEvent already no-ops on SSR / missing gtag, but guard the
  // outer call anyway since trackEvent's signature is permissive.
  try {
    trackEvent(
      name,
      payload as Record<string, string | number | boolean | null | undefined>,
    );
  } catch {
    // GA4 wrapper already swallows but defend anyway
  }

  // PostHog — only fire when the client has fully loaded posthog-js so
  // we don't race the provider's useEffect init. Wrap in try/catch so
  // SSR / blocked-tracker / disabled-consent paths can't surface errors.
  try {
    if (typeof window !== "undefined") {
      const ph = posthog as unknown as {
        __loaded?: boolean;
        capture?: typeof posthog.capture;
      };
      if (ph.__loaded && typeof ph.capture === "function") {
        ph.capture(name, payload);
      }
    }
  } catch {
    // posthog-js init can race; swallow rather than surface
  }
}
